import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import MovieCard from '../../components/MovieCard/MovieCard';
import { moviesDatabase } from '../../utils/database';
import './MoviesPage.css';

const MoviesPage = () => {
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const moviesPerPage = 12;
  
  const [filters, setFilters] = useState({
    genre: '',
    year: '',
    rating: '',
    sortBy: 'popularity'
  });

  // Get unique values for filters
  const genres = [...new Set(moviesDatabase.flatMap(movie => movie.categories))];
  const years = [...new Set(moviesDatabase.map(movie => movie.year))].sort((a, b) => b - a);

  useEffect(() => {
    // Filter out only movies (not TV series - for this demo all are movies)
    const moviesList = moviesDatabase.filter(movie => 
      !movie.title.toLowerCase().includes('series')
    );
    setMovies(moviesList);
    setFilteredMovies(moviesList);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, movies]);

  const applyFilters = () => {
    let results = [...movies];

    if (filters.genre) {
      results = results.filter(movie => 
        movie.categories.includes(filters.genre)
      );
    }

    if (filters.year) {
      results = results.filter(movie => movie.year === filters.year);
    }

    if (filters.rating) {
      const minRating = parseFloat(filters.rating);
      results = results.filter(movie => movie.rating >= minRating);
    }

    // Sorting
    switch (filters.sortBy) {
      case 'rating':
        results.sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        results.sort((a, b) => parseInt(b.year) - parseInt(a.year));
        break;
      case 'title':
        results.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'popularity':
      default:
        results.sort((a, b) => parseInt(b.votes.replace(/,/g, '')) - parseInt(a.votes.replace(/,/g, '')));
        break;
    }

    setFilteredMovies(results);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      genre: '',
      year: '',
      rating: '',
      sortBy: 'popularity'
    });
  };

  // Pagination
  const indexOfLastMovie = currentPage * moviesPerPage;
  const indexOfFirstMovie = indexOfLastMovie - moviesPerPage;
  const currentMovies = filteredMovies.slice(indexOfFirstMovie, indexOfLastMovie);
  const totalPages = Math.ceil(filteredMovies.length / moviesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="movies-page">
      {/* Hero Banner */}
      <div className="movies-page__hero" data-aos="fade-down">
        <div className="movies-page__hero-content">
          <h1 className="movies-page__hero-title">Movies</h1>
          <p className="movies-page__hero-subtitle">
            Discover our extensive collection of movies from all genres
          </p>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="movies-page__controls">
        <div className="movies-page__controls-left">
          <button 
            className={`movies-page__filter-btn ${showFilters ? 'movies-page__filter-btn--active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterListIcon />
            <span>Filters</span>
            {Object.values(filters).filter(v => v && v !== 'popularity').length > 0 && (
              <span className="movies-page__filter-badge">
                {Object.values(filters).filter(v => v && v !== 'popularity').length}
              </span>
            )}
          </button>
          <span className="movies-page__count">
            {filteredMovies.length} movies found
          </span>
        </div>

        <div className="movies-page__controls-right">
          <button
            className={`movies-page__view-btn ${viewMode === 'grid' ? 'movies-page__view-btn--active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <ViewModuleIcon />
          </button>
          <button
            className={`movies-page__view-btn ${viewMode === 'list' ? 'movies-page__view-btn--active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <ViewListIcon />
          </button>
        </div>
      </div>

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="movies-page__filters"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="movies-page__filters-inner">
              <select
                className="movies-page__filter-select"
                value={filters.genre}
                onChange={(e) => setFilters({ ...filters, genre: e.target.value })}
              >
                <option value="">All Genres</option>
                {genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>

              <select
                className="movies-page__filter-select"
                value={filters.year}
                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              >
                <option value="">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              <select
                className="movies-page__filter-select"
                value={filters.rating}
                onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
              >
                <option value="">Any Rating</option>
                <option value="9">9+ ⭐</option>
                <option value="8">8+ ⭐</option>
                <option value="7">7+ ⭐</option>
                <option value="6">6+ ⭐</option>
              </select>

              <select
                className="movies-page__filter-select"
                value={filters.sortBy}
                onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
              >
                <option value="popularity">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="year">Newest First</option>
                <option value="title">Alphabetical</option>
              </select>

              <button 
                className="movies-page__filter-reset"
                onClick={resetFilters}
              >
                Reset Filters
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Movies Grid/List */}
      <div className="movies-page__content">
        <motion.div 
          className={`movies-page__${viewMode}`}
          layout
        >
          {currentMovies.map((movie, index) => (
            <motion.div
              key={movie.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <MovieCard 
                movie={movie}
                index={index}
                variant={viewMode === 'list' ? 'horizontal' : 'poster'}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="movies-page__pagination">
            <button
              className="movies-page__pagination-btn"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <div className="movies-page__pagination-numbers">
              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                const showPage = pageNumber === 1 || 
                                pageNumber === totalPages || 
                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);
                
                if (showPage) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`movies-page__pagination-number ${
                        currentPage === pageNumber ? 'movies-page__pagination-number--active' : ''
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                  return <span key={pageNumber}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="movies-page__pagination-btn"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoviesPage;